import { api } from '@/api';
import { InjectionKey, onMounted, onUnmounted, Ref, ref, watchEffect } from 'vue';
import { injectStrict } from './injectStrict';
import { waitForConnectionState } from './signalR';
import { HubConnection, HubConnectionState } from '@microsoft/signalr';
import { WorkflowCommunicationHubKey } from '@/components/measurement/utils/types';
import { NotificationEntry } from '@/components/notifications/types';
import { createEventHook, EventHookOn } from '@vueuse/core';

interface ReachableHttpOptions {
  method: 'http';
  interval?: number;
}

interface ReachableSignalROptions {
  method: 'signalR';
  hubInjectionKey?: InjectionKey<HubConnection>;
}

interface ReachableReturnType {
  reachable: Ref<boolean | undefined>;
  onConnectionLost: EventHookOn<NotificationEntry>;
}

export function useBackendReachable(options: ReachableHttpOptions): ReachableReturnType;
export function useBackendReachable(options: ReachableSignalROptions): ReachableReturnType;
export function useBackendReachable(
  options: ReachableHttpOptions | ReachableSignalROptions,
): ReachableReturnType {
  let reachable: Ref<boolean | undefined>;

  const connectionLostHook = createEventHook();

  switch (options.method) {
    case 'signalR': {
      const defaults = {
        hubInjectionKey: WorkflowCommunicationHubKey,
      };

      reachable = useBackendReachableSignalR(options.hubInjectionKey ?? defaults.hubInjectionKey);
      break;
    }
    case 'http':
    default: {
      const defaults = {
        interval: 1000,
      };

      reachable = useBackendReachablePing(options.interval ?? defaults.interval);
    }
  }

  watchEffect(() => {
    if (!reachable.value) {
      connectionLostHook.trigger();
    }
  });

  return {
    reachable,
    onConnectionLost: connectionLostHook.on,
  };
}

function useBackendReachablePing(interval: number) {
  async function ping(interval: number) {
    try {
      await api.systemApi.pingRaw({
        method: 'HEAD',
        cache: 'no-cache',
        signal: AbortSignal.timeout(interval),
      });

      return true;
    } catch (error: unknown) {
      return false;
    }
  }

  const reachable = ref<boolean>(true);

  let intervalHandle: ReturnType<typeof setInterval> | undefined = undefined;

  onMounted(() => {
    intervalHandle = setInterval(async () => {
      reachable.value = await ping(interval);
    }, interval);
  });

  onUnmounted(() => {
    clearTimeout(intervalHandle);
  });

  return reachable;
}

function useBackendReachableSignalR(hubInjectionKey: InjectionKey<HubConnection>) {
  const reachable = ref<boolean | undefined>(true);

  const connection = injectStrict(hubInjectionKey);

  connection.onclose(() => {
    reachable.value = false;
  });

  connection.onreconnecting(() => {
    reachable.value = false;
  });

  connection.onreconnected(() => {
    reachable.value = true;
  });

  onMounted(async () => {
    if (connection.state === 'Disconnected') {
      await connection.start();
    }

    await waitForConnectionState(connection, HubConnectionState.Connected);
    reachable.value = connection.state === 'Connected';
  });

  return reachable;
}
